var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { CommandSocketInitializer } from './CommandSocketInitializer';
import { Timeouts } from './Timeouts';
const emptyConfigInfo = {
    channelKeyboardType: 'numeric',
    hasChannelKeyboard: false,
    hasMute: false,
    hasPower: false,
    hasVolume: false,
    hasVolumeMute: false,
    isMuteToggle: false,
    isPowerToggle: false,
    isVolumeMuteToggle: false,
    powerOffBusyMs: 0,
    powerOnBusyMs: 0,
    videoInputChangeBusyMs: 0,
};
const moduleTypeInfo = {
    ethernet: { type: 'ethernet' },
    ir: { type: 'ir' },
    ir_in: { type: 'ir', subType: 'in' },
    ir_out: { type: 'ir', subType: 'out' },
    irblaster: { type: 'irBlaster' },
    ir_blaster: { type: 'irBlaster' },
    irtriport: { type: 'irTriPort' },
    irtriportblaster: { type: 'irTriPortBlaster' },
    irtriport_blaster: { type: 'irTriPortBlaster' },
    module: { type: 'module' },
    relay: { type: 'relay' },
    relay_spst_3a: { type: 'relay', subType: 'spst-3a' },
    relaysensor: { type: 'relaySensor' },
    sensor_digital: { type: 'sensor', subType: 'digital' },
    serial: { type: 'serial' },
    serial_rs232: { type: 'serial', subType: 'rs232' },
    'switch_hdmi_3:1': { type: 'switch', subType: 'hdmi-3-1' },
    wifi: { type: 'wifi' },
};
export class UniversalIrEngine {
    constructor(socket, state, log, framework) {
        this.socket = socket;
        this.state = state;
        this.log = log;
        // other class properties and constructor
        this.configInfo = emptyConfigInfo;
        this.irConnector = '1';
        this.lastVideoInput = 'unknown';
        this.module = '1';
        this.videoInputConfig = {};
        this.initializerSteps = [
            {
                errorCode: 'verifyFailed',
                send: `ping`,
                next: /ERR(?:\s*|_)\d+|unknowncommand\s*\d+/i,
            },
        ];
        this.timeouts = new Timeouts();
        this.busyTimeout = this.timeouts.create();
        // controls
        this.busy = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.busy.value;
            }),
        };
        this.channelKeyboardType = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () { return this.configInfo.channelKeyboardType; }),
            hasControl: () => __awaiter(this, void 0, void 0, function* () { return this.configInfo.hasChannelKeyboard; }),
        };
        this.mediaOperation = {
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                if (this.configInfo.videoInputOperations) {
                    const videoInput = this.state.videoInput.value !== 'unknown'
                        ? this.state.videoInput.value
                        : this.lastVideoInput;
                    const operations = this.configInfo.videoInputOperations[videoInput];
                    if (!operations)
                        throw new Error(`no media operations for input ${videoInput}`);
                    yield this.doMediaOperation(operations, value, tags);
                }
                else {
                    const operations = this.configInfo.operations;
                    if (!operations)
                        throw new Error(`no media operations defined`);
                    yield this.doMediaOperation(operations, value, tags);
                }
                return value;
            }),
            hasControl: () => __awaiter(this, void 0, void 0, function* () { return this.configInfo.mediaOperationCaps !== undefined; }),
        };
        this.mediaOperations = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                var _a;
                return (_a = this.configInfo.mediaOperationCaps) !== null && _a !== void 0 ? _a : {};
            }),
            hasControl: () => __awaiter(this, void 0, void 0, function* () { return this.configInfo.mediaOperationCaps !== undefined; }),
        };
        this.mute = {
            set: (mute, tags) => __awaiter(this, void 0, void 0, function* () {
                var _b, _c;
                if (this.configInfo.isMuteToggle) {
                    const code = (_b = this.configInfo.operations) === null || _b === void 0 ? void 0 : _b.muteToggle;
                    if (code === undefined)
                        throw new Error(`muteToggle undefined`);
                    yield this.sendIrCode(code, tags);
                }
                else {
                    const op = mute ? 'muteOn' : 'muteOff';
                    const code = (_c = this.configInfo.operations) === null || _c === void 0 ? void 0 : _c[op];
                    if (code === undefined)
                        throw new Error(`${op} undefined`);
                    yield this.sendIrCode(code, tags);
                }
                return mute;
            }),
            hasControl: () => __awaiter(this, void 0, void 0, function* () { return this.configInfo.hasMute; }),
        };
        this.power = {
            set: (power, tags) => __awaiter(this, void 0, void 0, function* () {
                var _d, _e, _f;
                if ((_d = this.configInfo.isPowerToggle) !== null && _d !== void 0 ? _d : true) {
                    const code = (_e = this.configInfo.operations) === null || _e === void 0 ? void 0 : _e.powerToggle;
                    if (code === undefined)
                        throw new Error(`powerToggle undefined`);
                    yield this.sendIrCode(code, tags);
                }
                else {
                    const op = power ? 'powerOn' : 'powerOff';
                    const code = (_f = this.configInfo.operations) === null || _f === void 0 ? void 0 : _f[op];
                    if (code === undefined)
                        throw new Error(`${op} undefined`);
                    yield this.sendIrCode(code, tags);
                }
                const delayMs = power ? this.configInfo.powerOnBusyMs : this.configInfo.powerOffBusyMs;
                if (delayMs > 0) {
                    this.state.busy.update(true);
                    this.busyTimeout.set(() => this.state.busy.update(false), delayMs);
                }
                return power;
            }),
            isToggle: () => { var _a; return (_a = this.configInfo.isPowerToggle) !== null && _a !== void 0 ? _a : true; },
            hasControl: () => __awaiter(this, void 0, void 0, function* () { return this.configInfo.hasPower; }),
        };
        this.videoInput = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                var _g, _h;
                return (_h = (_g = Object.values(this.videoInputConfig)[0]) === null || _g === void 0 ? void 0 : _g.code) !== null && _h !== void 0 ? _h : '';
            }),
            set: (code, tags) => __awaiter(this, void 0, void 0, function* () {
                var _j;
                const irCode = (_j = this.configInfo.videoInputIrCodes) === null || _j === void 0 ? void 0 : _j[code];
                if (irCode === undefined)
                    throw new Error(`no ir code for input ${code}`);
                yield this.sendIrCode(irCode, tags);
                const delayMs = this.configInfo.videoInputChangeBusyMs;
                if (delayMs > 0) {
                    this.state.busy.update(true);
                    this.busyTimeout.set(() => this.state.busy.update(false), delayMs);
                }
                this.lastVideoInput = code; // Store value in case channel favorites are updated.
                return code;
            }),
            hasControl: () => __awaiter(this, void 0, void 0, function* () { return this.configInfo.videoInputIrCodes !== undefined; }),
            isStateless: (config) => { var _a; return Object.keys((_a = config.videoInputs) !== null && _a !== void 0 ? _a : {}).length !== 1; },
        };
        this.videoInputs = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                var _k;
                return (_k = this.configInfo.videoInputs) !== null && _k !== void 0 ? _k : {};
            }),
            hasControl: () => __awaiter(this, void 0, void 0, function* () { return this.configInfo.videoInputs !== undefined; }),
        };
        this.volume = {
            set: (volume, tags) => __awaiter(this, void 0, void 0, function* () {
                var _l, _m;
                if (volume < 0) {
                    const code = (_l = this.configInfo.operations) === null || _l === void 0 ? void 0 : _l.volumeDown;
                    if (code === undefined)
                        throw new Error(`volumeDown undefined`);
                    yield this.sendIrCode(code, tags);
                }
                else {
                    const code = (_m = this.configInfo.operations) === null || _m === void 0 ? void 0 : _m.volumeUp;
                    if (code === undefined)
                        throw new Error(`volumeUp undefined`);
                    yield this.sendIrCode(code, tags);
                }
                return volume;
            }),
            hasControl: () => __awaiter(this, void 0, void 0, function* () { return this.configInfo.hasVolume; }),
        };
        this.volumeMute = {
            set: (mute, tags) => __awaiter(this, void 0, void 0, function* () {
                var _o, _p, _q;
                if ((_o = this.configInfo.isVolumeMuteToggle) !== null && _o !== void 0 ? _o : true) {
                    const code = (_p = this.configInfo.operations) === null || _p === void 0 ? void 0 : _p.volumeMuteToggle;
                    if (code === undefined)
                        throw new Error(`volumeMuteToggle undefined`);
                    yield this.sendIrCode(code, tags);
                }
                else {
                    const op = mute ? 'volumeMuteOn' : 'volumeMuteOff';
                    const code = (_q = this.configInfo.operations) === null || _q === void 0 ? void 0 : _q[op];
                    if (code === undefined)
                        throw new Error(`${op} undefined`);
                    yield this.sendIrCode(code, tags);
                }
                return mute;
            }),
            isToggle: () => { var _a; return (_a = this.configInfo.isPowerToggle) !== null && _a !== void 0 ? _a : true; },
            hasControl: () => __awaiter(this, void 0, void 0, function* () { return this.configInfo.hasVolumeMute; }),
        };
        // polling
        this.polls = [
            {
                intervalMs: 4000,
                function: () => __awaiter(this, void 0, void 0, function* () {
                    if (this.socket.hasCommands())
                        return;
                    yield this.socket.send('ping', /ERR(?:\s*|_)\d+|unknowncommand\s*\d+/i, ['poll']);
                }),
            },
        ];
    }
    // phase callbacks
    onBeforeConfigure(config) {
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            this.configInfo = this.computeConfigInfo(config);
            this.irConnector = (_b = (_a = config.ir) === null || _a === void 0 ? void 0 : _a.connector) !== null && _b !== void 0 ? _b : '1';
            this.videoInputConfig = (_c = config.videoInputs) !== null && _c !== void 0 ? _c : {};
            this.timeouts.clearAll();
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    onInitialize(credentials) {
        return __awaiter(this, void 0, void 0, function* () {
            const result = yield CommandSocketInitializer.initialize(this.socket, this.initializerSteps, credentials);
            if (result === 'connected') {
                const modules = [];
                // Also work around documented bugs in Flex responses by detecting the model number and
                // hard-coding the ports for that device.
                const tags = ['initialize'];
                const match = yield this.socket.send(`getversion`, /(?:version,\d+,)?(.+)/, tags);
                const isFlex = match[1].startsWith('710-3000-');
                const id = this.socket.registerAsync(/device,(\d+),(\d+)\s+(\S+)/, match => {
                    const address = parseInt(match[1], 10);
                    let portCount = isFlex ? 4 : parseInt(match[2], 10);
                    const typeInfo = moduleTypeInfo[match[3].toLowerCase()];
                    if (typeInfo === undefined) {
                        this.log.error(`unrecognized module type ${match[3]}`);
                    }
                    else {
                        // Ethernet port count is reported as 0, but is actually 1.
                        if (typeInfo.type === 'ethernet')
                            portCount = 1;
                        modules.push(Object.assign({ address,
                            portCount, type: typeInfo.type }, (typeInfo.subType ? { subType: typeInfo.subType } : undefined)));
                    }
                });
                try {
                    yield this.socket.send(`getdevices`, /^endlistdevices/, tags);
                }
                finally {
                    this.socket.unregisterAsync(id);
                }
                // Find the first IR port and use its address as the module.
                for (const module of modules) {
                    if (module.type === 'ir') {
                        this.module = module.address.toString();
                        break;
                    }
                }
            }
            return result;
        });
    }
    // other class methods
    buildMediaOperationCaps(operations, mediaOperationCaps) {
        if (mediaOperationCaps === undefined)
            mediaOperationCaps = [];
        const ops = [
            'back',
            'channelPrevious',
            'channelTunerToggle',
            'closedCaptioningToggle',
            'directionEnter',
            'exit',
            'guide',
            'home',
            'info',
            'keyDash',
            'keyEnter',
            'mediaPlayerForward',
            'mediaPlayerNext',
            'mediaPlayerPause',
            'mediaPlayerPlay',
            'mediaPlayerPlayToggle',
            'mediaPlayerPrevious',
            'mediaPlayerRecord',
            'mediaPlayerRecordToggle',
            'mediaPlayerReverse',
            'mediaPlayerSkipAhead',
            'mediaPlayerSkipBack',
            'mediaPlayerStop',
            'mediaRecorderPause',
            'mediaRecorderRecord',
            'mediaRecorderRecordToggle',
            'mediaRecorderStop',
            'mediaStreamerForward',
            'mediaStreamerNext',
            'mediaStreamerPause',
            'mediaStreamerPrevious',
            'mediaStreamerRecord',
            'mediaStreamerRecordToggle',
            'mediaStreamerReverse',
            'mediaStreamerSkipAhead',
            'mediaStreamerSkipBack',
            'mediaStreamerStop',
            'mediaStreamerStream',
            'mediaStreamerStreamToggle',
            'menu',
        ];
        if (operations.key0 &&
            operations.key1 &&
            operations.key2 &&
            operations.key3 &&
            operations.key4 &&
            operations.key5 &&
            operations.key6 &&
            operations.key7 &&
            operations.key8 &&
            operations.key9 &&
            (operations.keyEnter || operations.keyEnterAuto)) {
            mediaOperationCaps.push('channelSet');
        }
        if (operations.channelUp && operations.channelDown) {
            mediaOperationCaps.push('channelUpDown');
        }
        if (operations.closedCaptioningOff && operations.closedCaptioningOn) {
            mediaOperationCaps.push('closedCaptioningSet');
        }
        if (operations.directionDown &&
            operations.directionLeft &&
            operations.directionRight &&
            operations.directionUp) {
            mediaOperationCaps.push('directionUpDownLeftRight');
        }
        if (operations.key0 &&
            operations.key1 &&
            operations.key2 &&
            operations.key3 &&
            operations.key4 &&
            operations.key5 &&
            operations.key6 &&
            operations.key7 &&
            operations.key8 &&
            operations.key9) {
            mediaOperationCaps.push('keyDigits');
        }
        for (const op of ops) {
            if (operations[op])
                mediaOperationCaps.push(op);
        }
        if (operations.pageDown && operations.pageUp) {
            mediaOperationCaps.push('pageUpDown');
        }
        return mediaOperationCaps;
    }
    computeConfigInfo(config) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        let irConfig;
        if (this.irConfigs) {
            irConfig = this.irConfigs[(_a = config.modelNumber) !== null && _a !== void 0 ? _a : ''];
            if (irConfig === undefined) {
                this.log.error(`model number ${config.modelNumber} not found in irConfigs`);
                return emptyConfigInfo;
            }
        }
        else {
            irConfig = this.irConfig ? this.irConfig : config.universalIr;
        }
        if (irConfig === undefined)
            return emptyConfigInfo;
        let channelKeyboardType = 'numeric';
        let hasChannelKeyboard = false;
        let hasMute = false;
        let hasPower = false;
        let hasVolume = false;
        let hasVolumeMute = false;
        let isMuteToggle = false;
        let isPowerToggle = false;
        let isVolumeMuteToggle = false;
        let mediaOperationCaps;
        const operations = (_b = irConfig.operations) !== null && _b !== void 0 ? _b : {};
        let videoInputs;
        let videoInputOperations;
        let videoInputIrCodes;
        if (operations.muteOff && operations.muteOn) {
            hasMute = true;
            isMuteToggle = false;
        }
        else if (operations.muteToggle) {
            hasMute = true;
            isMuteToggle = true;
        }
        if (operations.powerOff && operations.powerOn) {
            hasPower = true;
            isPowerToggle = operations.powerToggle !== undefined && ((_c = config.isPowerToggle) !== null && _c !== void 0 ? _c : false);
        }
        else if (operations.powerToggle) {
            hasPower = true;
            isPowerToggle = true;
        }
        if (operations.volumeDown && operations.volumeUp) {
            hasVolume = true;
        }
        if (operations.volumeMuteOff && operations.volumeMuteOn) {
            hasVolumeMute = true;
            isVolumeMuteToggle = false;
        }
        else if (operations.volumeMuteToggle) {
            hasVolumeMute = true;
            isVolumeMuteToggle = true;
        }
        if (irConfig.type === 'mediaPlayer') {
            const tmpMediaOperationCaps = this.buildMediaOperationCaps(operations);
            if (tmpMediaOperationCaps.includes('channelSet')) {
                hasChannelKeyboard = true;
                if (tmpMediaOperationCaps.includes('keyDash')) {
                    channelKeyboardType = 'numericWithDash';
                }
            }
            mediaOperationCaps = {};
            for (const videoInput of Object.values((_d = config.videoInputs) !== null && _d !== void 0 ? _d : {})) {
                mediaOperationCaps[videoInput.code] = tmpMediaOperationCaps;
            }
        }
        if (irConfig.type === 'display') {
            mediaOperationCaps = {};
            videoInputIrCodes = {};
            videoInputOperations = {};
            videoInputs = {};
            for (const [key, videoInput] of Object.entries((_e = irConfig.videoInputs) !== null && _e !== void 0 ? _e : {})) {
                if (typeof videoInput === 'string' || Array.isArray(videoInput)) {
                    videoInputIrCodes[key] = videoInput;
                }
                else {
                    const vi = videoInput;
                    videoInputIrCodes[key] = videoInput.select;
                    if (videoInput.operations) {
                        mediaOperationCaps[key] = this.buildMediaOperationCaps(videoInput.operations);
                        videoInputOperations[key] = videoInput.operations;
                        if (mediaOperationCaps[key].includes('channelSet')) {
                            hasChannelKeyboard = true;
                            if (mediaOperationCaps[key].includes('keyDash')) {
                                channelKeyboardType = 'numericWithDash';
                            }
                        }
                    }
                    if ('name' in videoInput) {
                        videoInputs[key] = Object.assign(Object.assign({ code: key }, (vi.description && { description: vi.description })), { icon: vi.icon, name: vi.name, isDynamic: true });
                    }
                }
            }
            if (Object.keys(mediaOperationCaps).length === 0)
                mediaOperationCaps = undefined;
            if (Object.keys(videoInputIrCodes).length === 0)
                videoInputIrCodes = undefined;
            if (Object.keys(videoInputOperations).length === 0)
                videoInputOperations = undefined;
            if (Object.keys(videoInputs).length === 0)
                videoInputs = undefined;
        }
        return Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({ channelKeyboardType,
            hasChannelKeyboard,
            hasMute,
            hasPower,
            hasVolume,
            hasVolumeMute,
            isMuteToggle,
            isPowerToggle,
            isVolumeMuteToggle }, (mediaOperationCaps && { mediaOperationCaps })), (operations && { operations })), { powerOffBusyMs: (_f = irConfig.powerOffBusyMs) !== null && _f !== void 0 ? _f : 4000, powerOnBusyMs: (_g = irConfig.powerOnBusyMs) !== null && _g !== void 0 ? _g : 4000, videoInputChangeBusyMs: (_h = irConfig.videoInputChangeBusyMs) !== null && _h !== void 0 ? _h : 2000 }), (videoInputIrCodes && { videoInputIrCodes })), (videoInputOperations && { videoInputOperations })), (videoInputs && { videoInputs }));
    }
    doMediaOperation(operations, value, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            if (value.operation === 'channelSet') {
                // Support channelSet by sending individual digits and enter or just the individual
                // digits on displays that don't have enter.
                let numSent = 0;
                for (const ch of value.channel) {
                    if (ch === '-') {
                        if (operations.keyDash === undefined) {
                            this.log.warning('keyDash not supported');
                        }
                        else {
                            yield this.sendMediaOperation(operations, 'keyDash', tags);
                        }
                    }
                    else if (ch === ',') {
                        yield this.timeouts.delay(250);
                    }
                    else {
                        const op = `key${ch}`;
                        yield this.sendMediaOperation(operations, op, tags);
                    }
                    numSent++;
                }
                if (numSent > 0 && 'keyEnter' in operations) {
                    yield this.sendMediaOperation(operations, 'keyEnter', tags);
                }
            }
            else if (value.operation === 'channelTunerSet') {
                throw new Error('TODO: implement channelTunerSet');
            }
            else if (value.operation === 'closedCaptioningSet') {
                throw new Error('TODO: implement closedCaptioningSet');
            }
            else {
                yield this.sendMediaOperation(operations, value.operation, tags);
            }
        });
    }
    sendIrCode(code, tags = []) {
        return __awaiter(this, void 0, void 0, function* () {
            if (Array.isArray(code)) {
                for (const item of code) {
                    if (typeof item === 'number') {
                        yield this.timeouts.delay(item);
                    }
                    else {
                        yield this.sendIrString(item, tags);
                    }
                }
            }
            else {
                yield this.sendIrString(code, tags);
            }
        });
    }
    sendMediaOperation(operations, operation, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const code = operations[operation];
            if (code === undefined)
                throw new Error(`${operation} operation not defined`);
            if (typeof code !== 'string' && !Array.isArray(code)) {
                throw new Error(`${operation} operation not an ir code`);
            }
            yield this.sendIrCode(code, tags);
        });
    }
    sendIrString(str, tags = []) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.socket.send(`sendir,${this.module}:${this.irConnector}${str}`, /completeir,\d:\d|busyIR/i, tags);
        });
    }
}
// static properties
UniversalIrEngine.driverType = 'socketDriver';
UniversalIrEngine.emulatorKey = '-MSeHd3FH9_LOy7AM0Ed';
UniversalIrEngine.baseSocketOptions = {
    commandExtractor: {
        terminators: ['\r'],
    },
    sendTerminator: '\r',
};
UniversalIrEngine.socketOptions = [
    Object.assign(Object.assign({}, UniversalIrEngine.baseSocketOptions), { port: 4998 }),
];
